import uniqueId from 'lodash/uniqueId';

import {
  DEFAULT_ANGLE,
  DEFAULT_CORNER_RADIUS,
  FREE_CORNER_RADIUS,
  UNIT_MEASURE,
} from '~/core/constants';
import { ProductType, ShapeType, isBumpOut, isRadiusOut } from '~/core/enums';
import '~/core/helpers';
import {
  getSegmentJointPoints,
  getSideItemJointPoints,
  isCountertopProduct,
} from '~/core/helpers';
import { calculateArcLength } from '~/core/helpers/MathFunctions.utils';
import {
  ApplianceType,
  ColorModel,
  CommonProductModel,
  CornerInfo,
  DrawingEdge,
  DrawingShape,
  MaterialModel,
  PricedProduct,
  ProductCatalogMap,
  ProductEstimateQuery,
  ProductQty,
} from '~/core/models';
import { SaveEstimateItem } from '~/core/models/save-estimate-dto';
import {
  buildQteAttributes,
  getJointPointsCount,
  getProductDescription,
  isActualProduct,
} from '~/core/utils';

export const lineItemsFromPricedProducts = (
  pricedProducts: PricedProduct[]
): SaveEstimateItem[] =>
  Object.entries(
    pricedProducts
      .filter(
        ({ productId, adjustedQty }) =>
          isActualProduct(productId) && adjustedQty > 0
      )
      .reduce<Record<number, number>>((acc, { productId, adjustedQty }) => {
        acc[productId] = (acc[productId] || 0) + adjustedQty;

        return acc;
      }, {})
  ).map(([productId, quantity]) => ({
    productId: Number(productId),
    quantity,
  }));

export interface QuoteLineItemsOptions {
  useMinQtyAreaForPricing: boolean;
  smallJobMinimumArea: number;
}

/**
 * Line items to persist with the estimate (multipart estimateItems,
 * estimateData.lineItems and the dirty check). Same as
 * lineItemsFromPricedProducts, except that the countertop line is raised to the
 * material minimum when the small-job rules price the minimum area (no override
 * ticket, no small project fee). Port of getQuoteLineItems from EE2 (SD-1913).
 *
 * It MUST run on the AGGREGATED output of lineItemsFromPricedProducts:
 * backsplash and waterfall are priced under the color's productId and merge
 * into the countertop line first, so bumping per priced product would count
 * the minimum twice (25 + 6 instead of 25).
 *
 * The countertop is the first isCountertopProduct match, as in EE2 and in
 * calculateTotalPrice (which sums the same predicate for the displayed total).
 * This relies on the color being the first product of the pricing query
 * (pricedProductsContext getProducts returns [pricedColor, ...]); getEEProductType
 * falls back to COUNTERTOP for unclassified products, so an unclassified product
 * placed before the color would take the bump instead.
 */
export const quoteLineItemsFromPricedProducts = (
  pricedProducts: PricedProduct[],
  { useMinQtyAreaForPricing, smallJobMinimumArea }: QuoteLineItemsOptions
): SaveEstimateItem[] => {
  const lineItems = lineItemsFromPricedProducts(pricedProducts);

  if (!useMinQtyAreaForPricing) {
    return lineItems;
  }

  const countertopProductId =
    pricedProducts.find(isCountertopProduct)?.productId;

  return lineItems.map((item) =>
    item.productId === countertopProductId
      ? { ...item, quantity: Math.max(item.quantity, smallJobMinimumArea) }
      : item
  );
};

/**
 * Filter out products we already have known prices for and use those known prices if present
 * unknownProductsQuery = query details for products that are not in our catalog (should be very rare)
 * completedPricedProducts = fully PricedProducts based on existing catalog data
 *
 * @param productEstimateQuery
 * @returns [unknownProductsQuery, completedPricedProducts]
 */
export const getPricingEstimate = ({
  productCatalog,
  productEstimateQuery,
  selectedColor,
}: {
  productCatalog: ProductCatalogMap;
  productEstimateQuery: ProductEstimateQuery;
  selectedColor?: ColorModel;
}): [ProductEstimateQuery, PricedProduct[]] => {
  const initialUnknownProductsQuery: ProductEstimateQuery = {
    zipCode: productEstimateQuery.zipCode,
    storeId: productEstimateQuery.storeId,
    products: [],
  };

  return productEstimateQuery.products.reduce<
    [ProductEstimateQuery, PricedProduct[]]
  >(
    ([unknownProductsQuery, completedPricedProducts], product) => {
      const catalogItem = productCatalog[product.productId];

      if (catalogItem) {
        const freeItemPromo = catalogItem.promos?.find((promo) =>
          promo.colorProductIds
            ? promo.colorProductIds
                .split(',')
                .some(
                  (promoColorId) =>
                    Number(promoColorId) === selectedColor?.productId
                )
            : true
        );

        // if there was a promo found, adjust quantity that's ONLY used for price calculations
        const adjustedQty = freeItemPromo
          ? Math.max(0, product.quantity - freeItemPromo.maxQty)
          : product.quantity;

        const existingPricedProduct: PricedProduct = {
          ...catalogItem,
          unitPrice: catalogItem.price,
          unitPriceWithoutDiscount: catalogItem.priceWithoutDiscount,
          lineItemId: product.lineItemId,
          listKey: uniqueId(), // TODO is this needed/correct?
          price: catalogItem.price * adjustedQty,
          priceWithoutDiscount: catalogItem.priceWithoutDiscount * adjustedQty,
          quantity: product.quantity,
          adjustedQty,
        };

        completedPricedProducts.push(existingPricedProduct);
      } else if (isActualProduct(product.productId)) {
        unknownProductsQuery.products.push(product);
      }

      return [unknownProductsQuery, completedPricedProducts];
    },
    [initialUnknownProductsQuery, []]
  );
};

/**
 * true if the adjacent edges for the given corner are both finished edges
 * @param shape
 * @param cornerID
 */
const isCornerAFinishedEdge = (
  shape: DrawingShape,
  cornerID: string
): boolean => {
  if (!shape.arrowsInfo || !shape.drawingEdges) {
    return false;
  }
  // get the IDs of the edges adjacent to the corner
  const edgeIds = shape.drawingEdges
    .filter((e) => e.startCornerId === cornerID || e.endCornerId === cornerID)
    .map((e) => e.id);

  // find the arrowsInfo objects that are associated with those edges
  const arrows = shape.arrowsInfo.filter((a) =>
    edgeIds.includes(a.drawingEdgeId || '')
  );

  // if either of the two adjacent edges is unfinished, then return false
  return !arrows.some((a) => !a.isActiveForEdgeCalc);
};

const calculateCornerEdgeLength = (size: number, angle: number): number => {
  const angleOfCircleInDegrees = 2 * (90 - Math.abs(angle / 2));

  return (2 * Math.PI * Math.floor(size) * angleOfCircleInDegrees) / 360;
};

const calculateRemovedStraightEdgeLength = (
  size: number,
  angle: number
): number => {
  const angleOfCornerInRadians = (angle * Math.PI) / 180;

  return Math.floor(size) / Math.tan(angleOfCornerInRadians / 2);
};

const isValidFinishedCorner = (
  drawingShape: DrawingShape,
  cornerRadius: CornerInfo
): boolean =>
  cornerRadius.productId !== 0 &&
  cornerRadius.size !== DEFAULT_CORNER_RADIUS &&
  isCornerAFinishedEdge(drawingShape, cornerRadius.id) &&
  !cornerRadius.diagonalLength;

/**
 * Calculates the total amount of linear inches of the edges of a shape
 * @param drawingShape the shape to calculate the edge quantity
 * @returns {number} linear inches of the edges
 */
export const getEdgeQuantity = (drawingShape: DrawingShape): number => {
  const _edgesQuantity = (drawingShape.arrowsInfo ?? [])
    .filter(
      (arrow) => arrow.size && !arrow.isInterior && arrow.isActiveForEdgeCalc
    )
    .reduce((qty, arrow) => {
      const arrowSize = arrow.size ?? 0;

      const [edge] = drawingShape.drawingEdges.filter(
        (edge: DrawingEdge) => arrow.drawingEdgeId === edge.id
      );

      if (edge) {
        if (isRadiusOut(edge)) {
          return qty + calculateArcLength(arrowSize, edge.radiusSagitta ?? 0);
        } else if (edge.bumpOut && isBumpOut(edge)) {
          return qty + arrowSize + edge.bumpOut.depth * 2;
        }
      }

      return qty + arrowSize;
    }, 0);

  // adjust the edge length total by subtracting out any radius corners straight edges and adding in the rounded length
  const totalEdgesQuantity = Object.values(drawingShape.cornerMap)
    .filter((cornerRadius) => isValidFinishedCorner(drawingShape, cornerRadius))
    .reduce((edgesQuantity, cornerRadius) => {
      const size = cornerRadius.size ?? 0;
      const angle = cornerRadius.angle ?? DEFAULT_ANGLE;

      const cornerEdgeLength = calculateCornerEdgeLength(size, angle);
      const removedStraightEdgeLength = calculateRemovedStraightEdgeLength(
        size,
        angle
      );

      return (
        edgesQuantity +
        Math.abs(cornerEdgeLength) -
        2 * Math.abs(removedStraightEdgeLength)
      );
    }, _edgesQuantity);

  return Math.ceil(totalEdgesQuantity);
};

export const getProductTypeFromApplianceType = (
  appliance: ApplianceType
): ProductType => {
  const mapApplianceTypeToProductType: Record<ApplianceType, ProductType> = {
    [ApplianceType.DISHWASHER]: ProductType.DISHWASHER,
    [ApplianceType.COOKTOP]: ProductType.COOKING_APPLIANCE,
    [ApplianceType.RANGE]: ProductType.COOKING_APPLIANCE,
    [ApplianceType.REFRIGERATOR]: ProductType.REFRIGERATOR,
    [ApplianceType.FAUCET]: ProductType.FAUCET,
    [ApplianceType.SINK]: ProductType.SINK,
    [ApplianceType.SINK_LABOR]: ProductType.SINK_LABOR,
    [ApplianceType.ELECTRICAL_CUTOUT]: ProductType.ELECTRICAL_CUTOUT,
    [ApplianceType.FAUCET_CUTOUT]: ProductType.FAUCET_CUTOUT,
  };

  return mapApplianceTypeToProductType[appliance];
};

/**
 * Accumulate duplicate corner radii
 */
const duplicateProductsReducer = (
  accumulatedPricedProducts: PricedProduct[],
  currentPricedProduct: PricedProduct
) => {
  const duplicatePricedProduct = accumulatedPricedProducts.findIndex(
    (pricedProduct) =>
      pricedProduct.productId === currentPricedProduct.productId &&
      !currentPricedProduct.lineItemId
  );

  if (duplicatePricedProduct !== -1) {
    accumulatedPricedProducts[duplicatePricedProduct] = {
      ...accumulatedPricedProducts[duplicatePricedProduct],
      quantity:
        accumulatedPricedProducts[duplicatePricedProduct].quantity +
        currentPricedProduct.quantity,
    };
  } else {
    accumulatedPricedProducts.push(currentPricedProduct);
  }

  return accumulatedPricedProducts;
};

/**
 * Aggregate data from request and client side
 * This method applies promos to cutouts
 */
export const getAggregatedPricedProducts = ({
  drawingShapes,
  pricedProducts,
  selectedColor,
  selectedMaterial,
}: {
  drawingShapes: DrawingShape[];
  pricedProducts: PricedProduct[];
  selectedColor?: ColorModel;
  selectedMaterial?: MaterialModel;
  //eslint-disable-next-line  sonarjs/cognitive-complexity
}): PricedProduct[] => {
  const aggregatedPricedProducts = [...pricedProducts];

  if (selectedMaterial && selectedColor?.jointProductId) {
    const jointPoint = selectedMaterial.jointPoints.find(
      (jointPoint) =>
        selectedColor.jointProductId &&
        jointPoint.productId === selectedColor.jointProductId
    );

    if (jointPoint && jointPoint.productId) {
      // pre-compute the total quantity and then add just one product line
      const jointPointQuantity = drawingShapes.reduce(
        (acc: number, drawingShape: DrawingShape) => {
          const segmentJointPoints = getSegmentJointPoints(
            drawingShape,
            selectedMaterial
          );
          const sideItemJointPoints = getSideItemJointPoints(
            drawingShape,
            selectedMaterial
          );

          return (
            acc +
            getJointPointsCount(drawingShape.skeleton) +
            segmentJointPoints +
            sideItemJointPoints
          );
        },
        0
      );

      const product: PricedProduct = {
        abbrevDesc: jointPoint.abbrevDesc,
        adjustedQty: jointPointQuantity,
        consumerDesc: jointPoint.consumerDesc,
        listKey: jointPoint?.productId?.toString(),
        name: getProductDescription(jointPoint as CommonProductModel),
        price: jointPoint.price * jointPointQuantity,
        priceWithoutDiscount:
          jointPoint.priceWithoutDiscount * jointPointQuantity,
        productId: jointPoint.productId,
        productType: ProductType.JOINT_POINT,
        quantity: jointPointQuantity,
        unitOfMeasure: jointPoint.unitOfMeasure,
        ...buildQteAttributes(jointPoint as CommonProductModel),
      };

      aggregatedPricedProducts.push(product);
    }
  }

  const reducedAggregatedPricedProducts: PricedProduct[] = drawingShapes
    .reduce((aggregatedPricedProducts, drawingShape) => {
      const cornerRadiiInfo = Object.values(drawingShape.cornerMap);

      // Get Corner Radius quantity
      const freeCornerRadiiQuantity = cornerRadiiInfo.filter(
        (cornerRadius) =>
          cornerRadius &&
          !cornerRadius.productId &&
          cornerRadius.isVisibleInCanvas &&
          !cornerRadius.diagonalLength
      ).length;

      if (!freeCornerRadiiQuantity) {
        return aggregatedPricedProducts;
      }

      // insert free corner radius on third position of priced products array
      const freeCornerRadius: PricedProduct = {
        listKey: `${FREE_CORNER_RADIUS.LIST_ITEM}_${uniqueId()}`,
        productId: FREE_CORNER_RADIUS.ID,
        name: FREE_CORNER_RADIUS.NAME,
        abbrevDesc: FREE_CORNER_RADIUS.NAME,
        consumerDesc: FREE_CORNER_RADIUS.NAME,
        productType: ProductType.CORNER_RADIUS,
        price: 0,
        priceWithoutDiscount: 0,
        quantity: freeCornerRadiiQuantity,
        adjustedQty: freeCornerRadiiQuantity,
        unitOfMeasure: UNIT_MEASURE.CORNER_RADIUS,
        qteCode: null,
        qteCodeId: null,
        qteSubCode: null,
        qteSubCodeId: null,
        qteTypeCode: null,
        qteTypeId: null,
      };

      aggregatedPricedProducts.splice(2, 0, freeCornerRadius);

      return aggregatedPricedProducts;
    }, aggregatedPricedProducts ?? [])
    .reduce(duplicateProductsReducer, []);

  // send the list ordered by productId
  return reducedAggregatedPricedProducts
    .filter((product) => product.quantity > 0)
    .sort((prevPricedProduct, currentPricedProduct) => {
      if (
        prevPricedProduct.productType === ProductType.CORNER_RADIUS &&
        currentPricedProduct.productType === ProductType.CORNER_RADIUS
      ) {
        return prevPricedProduct.productId &&
          currentPricedProduct.productId &&
          prevPricedProduct.productId > currentPricedProduct.productId
          ? 1
          : -1;
      }

      return 0;
    });
};

export const buildEdgesPrice = (
  drawingShapes: DrawingShape[]
): ProductQty[] => {
  const edges: ProductQty[] = [];

  drawingShapes.forEach((drawingShape: DrawingShape) => {
    if (
      drawingShape.edge === undefined ||
      drawingShape.type === ShapeType.FAUX_SHAPE
    ) {
      return;
    }
    const edgeQuantity = getEdgeQuantity(drawingShape);

    const existingEdge = edges.find(
      (existingEdge) => existingEdge.productId === drawingShape.edge?.productId
    );

    if (existingEdge !== undefined) {
      existingEdge.quantity += edgeQuantity;
    } else {
      edges.push({
        productId: drawingShape.edge.productId,
        quantity: edgeQuantity,
      });
    }
  });

  return edges;
};
